import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  api,
  clearSession,
  getEmail,
  getToken,
  hasLiveSession,
  renewSessionIfDue,
  saveSession,
} from "@/lib/api";

interface AuthState {
  email: string | null;
  preferredName: string | null;
  role: "user" | "admin" | "superadmin";
  loading: boolean;
}

interface AuthApi extends AuthState {
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  register: (
    email: string,
    password: string,
    inviteCode: string,
    acceptance: { termsAccepted: boolean; termsVersion: string; adultConfirmed: boolean; captchaToken?: string; messengerRegistrationToken?: string; referralCode?: string }
  ) => Promise<{ ok: boolean; error?: string; messengerLinked?: boolean }>;
  logout: () => void;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// Auth service response shapes.
interface LoginResponse {
  messengerLinked?: boolean;
  accessToken: string;
  expiresAt: string;
  userId: string;
  plan: string;
  region: string;
  role: "user" | "admin" | "superadmin";
}
interface MeResponse {
  userId: string;
  email: string;
  role: "user" | "admin" | "superadmin";
  plan: string;
  region: string;
  suspendedAt: string | null;
  preferredName: string | null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    email: getEmail(),
    preferredName: null,
    role: "user",
    loading: false,
  });

  const refreshMe = useCallback(async () => {
    if (!getToken()) return;
    const resp = await api.get<MeResponse>("/v1/me");
    if (!resp.ok) return;
    setState((s) => ({
      ...s,
      email: resp.data.email,
      preferredName: resp.data.preferredName,
      role: resp.data.role,
    }));
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Keep an active session alive: renew on open, when the tab becomes
  // visible again and hourly while it stays open.
  useEffect(() => {
    void renewSessionIfDue();
    const onVisible = () => {
      if (document.visibilityState === "visible") void renewSessionIfDue();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void renewSessionIfDue(), 60 * 60 * 1000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, []);

  // Sync across tabs.
  useEffect(() => {
    const onStorage = () => {
      setState((s) => ({ ...s, email: getEmail() }));
      refreshMe();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshMe]);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true }));
    const resp = await api.post<LoginResponse>("/v1/auth/login", { email, password });
    if (!resp.ok) {
      setState((s) => ({ ...s, loading: false }));
      return { ok: false, error: resp.error };
    }
    saveSession(resp.data.accessToken, email);
    setState({ email, preferredName: null, role: resp.data.role ?? "user", loading: false });
    return { ok: true };
  }, []);

  const register = useCallback(async (
    email: string, password: string, inviteCode: string,
    acceptance: { termsAccepted: boolean; termsVersion: string; adultConfirmed: boolean; captchaToken?: string; messengerRegistrationToken?: string; referralCode?: string }
  ) => {
    setState((s) => ({ ...s, loading: true }));
    const resp = await api.post<LoginResponse>("/v1/auth/register", {
      email,
      password,
      inviteCode,
      ...acceptance,
    });
    if (!resp.ok) {
      setState((s) => ({ ...s, loading: false }));
      return { ok: false, error: resp.error };
    }
    saveSession(resp.data.accessToken, email);
    setState({ email, preferredName: null, role: resp.data.role ?? "user", loading: false });
    return { ok: true, messengerLinked: resp.data.messengerLinked };
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setState({ email: null, preferredName: null, role: "user", loading: false });
  }, []);

  const value = useMemo<AuthApi>(
    () => ({ ...state, login, register, logout, refreshMe }),
    [state, login, register, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** True only for a stored token that has not expired yet. */
export function hasToken(): boolean {
  return hasLiveSession();
}
